import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Youtube } from 'lucide-react';
import { PHONE_TEL } from '@/config/business';
import { Button } from '@/components/ui/button';
import { openCookieSettings } from '@/lib/advertisingConsent';

const SEOFooter: React.FC<{ showCookieSettings?: boolean }> = ({ showCookieSettings = false }) => {
  return (
    <>
      <footer className="bg-gradient-to-br from-primary via-secondary to-primary text-white">
        {/* Main Footer Content */}
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Column 1: Best-Drive Bath */}
            <div className="space-y-4">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-violet via-rose to-sky bg-clip-text text-transparent">BEST-DRIVE</h3>
              <p className="text-white/80 text-sm leading-relaxed">
                DVSA Grade A Driving Instructor in Bath. Modern coaching, high pass rates, weekly lessons and intensive courses.
              </p>
              <div className="space-y-2 pt-2">
                <a 
                  href={PHONE_TEL} 
                  className="flex items-center gap-2 text-white/80 hover:text-sky transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  <span>07564 936033</span>
                </a>
                <a 
                  href="mailto:help@best-drive.co.uk" 
                  className="flex items-center gap-2 text-white/80 hover:text-rose transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  <span>help@best-drive.co.uk</span>
                </a>
                <div className="flex items-center gap-2 text-white/80">
                  <MapPin className="h-4 w-4" />
                  <span>Bath, Somerset</span>
                </div>
                <a 
                  href="https://youtube.com/@best-drive?si=4kloJ2QV7603eYGP" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/80 hover:text-red-400 transition-colors"
                >
                  <Youtube className="h-4 w-4" />
                  <span>YouTube Channel</span>
                </a>
              </div>
            </div>

            {/* Column 2: Lessons & Courses */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Lessons & Courses</h4>
              <nav className="flex flex-col space-y-2">
                <Link to="/book-now" className="text-white/80 hover:text-emerald transition-colors text-sm">
                  Book an Assessment
                </Link>
                <Link to="/why-best-drive" className="text-white/80 hover:text-rose transition-colors text-sm">
                  Why Best-Drive
                </Link>
                <Link to="/book-now" className="text-white/80 hover:text-violet transition-colors text-sm">
                  Intensive Course Bookings
                </Link>
                <Link to="/prices" className="text-white/80 hover:text-amber transition-colors text-sm">
                  Intensive Course Pricing
                </Link>
                <Link to="/intensive-driving-courses-bath" className="text-white/80 hover:text-sky transition-colors text-sm">
                  Intensive Driving Courses Bath
                </Link>
                <Link to="/intensive-driving-courses-chippenham" className="text-white/80 hover:text-emerald transition-colors text-sm">
                  Intensive Driving Courses Chippenham
                </Link>
                <Link to="/intensive-driving-courses-kingswood" className="text-white/80 hover:text-rose transition-colors text-sm">
                  Intensive Driving Courses Kingswood
                </Link>
                <Link to="/areas-covered" className="text-white/80 hover:text-sky transition-colors text-sm">
                  Areas I Cover
                </Link>
              </nav>
            </div>

            {/* Column 3: Tests & Guides */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Tests & Guides</h4>
              <nav className="flex flex-col space-y-2">
                <Link to="/#last-minute-support" className="text-white/80 hover:text-rose transition-colors text-sm">
                  Last Minute Driving Test Cover
                </Link>
                <Link to="/reviews" className="text-white/80 hover:text-amber transition-colors text-sm">
                  Reviews
                </Link>
                <Link to="/blog" className="text-white/80 hover:text-sky transition-colors text-sm">
                  Guides & Tips
                </Link>
                <Link to="/contact" className="text-white/80 hover:text-emerald transition-colors text-sm">
                  Contact
                </Link>
                <Link to="/new-driver-essentials" className="text-white/80 hover:text-violet transition-colors text-sm">
                  Insurance and Tax, MOT
                </Link>
                <Link to="/become-a-driving-instructor" className="text-white/80 hover:text-amber transition-colors text-sm font-semibold">
                  Become a Driving Instructor
                </Link>
                <Link to="/before-your-test" className="text-white/80 hover:text-sky transition-colors text-sm">
                  Before Your Driving Test
                </Link>
                <Link to="/mock-tests" className="text-white/80 hover:text-emerald transition-colors text-sm">
                  Mock Tests
                </Link>
                <Link to="/your-driving-journey" className="text-white/80 hover:text-violet transition-colors text-sm">
                  Your Driving Journey
                </Link>
                <Link to="/nervous-drivers" className="text-white/80 hover:text-rose transition-colors text-sm">
                  Nervous Drivers
                </Link>
                <Link to="/learning-difficulties" className="text-white/80 hover:text-amber transition-colors text-sm">
                  Learning Difficulties Support
                </Link>
                <Link to="/refresher-courses" className="text-white/80 hover:text-sky transition-colors text-sm">
                  Refresher Courses
                </Link>
                <Link to="/after-you-pass" className="text-white/80 hover:text-emerald transition-colors text-sm">
                  After You Pass
                </Link>
                <Link to="/parent-learning" className="text-white/80 hover:text-violet transition-colors text-sm">
                  Advice for Parents
                </Link>
                <Link to="/clear-path-quiz" className="text-white/80 hover:text-rose transition-colors text-sm">
                  Clear Path Quiz
                </Link>
                <Link to="/about-ian" className="text-white/80 hover:text-amber transition-colors text-sm">
                  About Ian
                </Link>
              </nav>
            </div>

            {/* Column 4: Legal */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Legal</h4>
              <nav className="flex flex-col space-y-2">
                <Link to="/terms" className="text-white/80 hover:text-violet transition-colors text-sm">
                  Terms & Conditions
                </Link>
                <Link to="/privacy-policy" className="text-white/80 hover:text-rose transition-colors text-sm">
                  Privacy Policy
                </Link>
                <Link to="/cookies-policy" className="text-white/80 hover:text-sky transition-colors text-sm">
                  Cookies Policy
                </Link>
                {showCookieSettings && <Button type="button" variant="link" onClick={() => window.setTimeout(openCookieSettings, 0)} className="h-auto min-h-11 justify-start px-0 text-sm text-foreground underline underline-offset-4">Cookie settings</Button>}
              </nav>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20">
          <div className="container mx-auto max-w-6xl px-4 py-4 space-y-2">
            <p className="text-center text-white/70 text-sm">
              © 2025 Best-Drive Bath — All Rights Reserved
            </p>
            <p className="text-center text-white/75 text-xs">
              AVARO SERVICES LTD · Company number 16360481 · 3rd Floor, 86-90 Paul Street, London, England, EC2A 4NE
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default SEOFooter;
